// src/pages/AdminChatPanel.jsx
import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import { io } from 'socket.io-client';
import { 
  Send, 
  Paperclip, 
  X, 
  User, 
  MessageCircle, 
  Check, 
  CheckCheck, 
  ArrowLeft,
  ArrowRight
} from 'lucide-react';
import { motion } from 'framer-motion';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '/';

export default function AdminChatPanel() {
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  const [file, setFile] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const token = localStorage.getItem('authToken');
  const navigate = useNavigate();

  // ریسپانسیو
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // دریافت لیست اتاق‌ها
  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const res = await api.get('/api/chat/admin/rooms', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRooms(res.data.rooms);
      } catch (err) {
        Swal.fire({ icon: 'error', text: 'خطا در دریافت اتاق‌ها', toast: true });
      }
    };
    fetchRooms();
  }, [token]);

  // اتصال سوکت
  useEffect(() => {
    if (!selectedRoom) return;

    socketRef.current = io(API_URL, {
      auth: { token },
      transports: ['polling', 'websocket'],
      path: '/socket.io'
    });

    const socket = socketRef.current;

    socket.emit('admin_join_room', { room_id: selectedRoom.id });

    socket.on('previous_messages', (msgs) => {
      setMessages(msgs);
    });

    socket.on('receive_message', (msg) => {
      setMessages(prev => [...prev, { ...msg, animate: true }]);
      setIsTyping(false);
    });

    socket.on('typing', () => setIsTyping(true));
    socket.on('stop_typing', () => setIsTyping(false));

    socket.on('connect', () => console.log('ادمین وصل شد'));

    return () => {
      socket.disconnect();
    };
  }, [selectedRoom, token]);

  // تایپ ادمین
  useEffect(() => {
    if (!message.trim() || !selectedRoom || !socketRef.current) return;

    socketRef.current.emit('typing', { room_id: selectedRoom.id });
    const timeout = setTimeout(() => {
      socketRef.current?.emit('stop_typing', { room_id: selectedRoom.id });
    }, 1000);

    return () => clearTimeout(timeout);
  }, [message, selectedRoom]);

  // دریافت unread_count
  useEffect(() => {
    if (!socketRef.current) return;
    const socket = socketRef.current;

    socket.on('unread_count', ({ room_id }) => {
      setRooms(prev => prev.map(r => 
        r.id === room_id ? { ...r, unread_count: (r.unread_count || 0) + 1 } : r
      ));
    });

    socket.on('unread_cleared', ({ room_id }) => {
      setRooms(prev => prev.map(r => 
        r.id === room_id ? { ...r, unread_count: 0 } : r
      ));
    });

    socket.on('unread_count_updated', ({ room_id, count }) => {
      setRooms(prev => prev.map(r => 
        r.id === room_id ? { ...r, unread_count: count } : r
      ));
    });

    return () => {
      socket.off('unread_count');
      socket.off('unread_cleared');
      socket.off('unread_count_updated');
    };
  }, []);

  // اسکرول
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // ارسال پیام
  const sendMessage = async () => {
    if (!message.trim() && !file) return;

    let file_url = null;
    let file_type = null;

    if (file) {
      const form = new FormData();
      form.append('file', file);
      try {
        const res = await api.post('/api/chat/upload', form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        file_url = res.data.file_url;
        file_type = res.data.file_type;
      } catch (err) {
        Swal.fire({ icon: 'error', text: 'خطا در آپلود', toast: true });
        return;
      }
    }

    socketRef.current.emit('admin_send_message', {
      room_id: selectedRoom.id,
      message: message.trim(),
      file_url,
      file_type
    });

    setMessage('');
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // انتخاب اتاق
  const selectRoom = (room) => {
    setSelectedRoom(room);
    if (isMobile) {
      document.getElementById('sidebar').style.display = 'none';
      document.getElementById('chat').style.display = 'flex';
    }
  };

  // برگشت
  const goBack = () => {
    if (isMobile) {
      document.getElementById('sidebar').style.display = 'block';
      document.getElementById('chat').style.display = 'none';
      setSelectedRoom(null);
    }

    // فقط بگو ترک کردی — عدد خودش درست میشه
    if (socketRef.current && selectedRoom) {
      socketRef.current.emit('admin_leave_room', { room_id: selectedRoom.id });
    }
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* سایدبار */}
      <div 
        id="sidebar" 
        className={`${isMobile && selectedRoom ? 'hidden' : 'flex'} flex-col w-full md:w-80 bg-white border-r border-gray-200`}
      >
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-indigo-600" />
            کاربران
          </h2>
          <button
            onClick={() => navigate('/')}
            className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition"
            title="برگشت به داشبورد"
          >
            <ArrowRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {rooms.length === 0 ? (
            <p className="text-center text-gray-500 mt-8">هیچ کاربری نیست</p>
          ) : (
            rooms.map(room => (
              <button
                key={room.id}
                onClick={() => selectRoom(room)}
                className={`w-full p-3 rounded-lg text-right transition mb-1 ${
                  selectedRoom?.id === room.id ? 'bg-indigo-600 text-white' : 'hover:bg-gray-100'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1 text-right">
                    <div className="font-medium">{room.user_name}</div>
                    <div className="text-xs opacity-70 mt-1">{room.last_message}</div>
                  </div>
                  <div className="text-left">
                    {room.unread_count > 0 && (
                      <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5 min-w-[20px]">
                        {room.unread_count}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-xs opacity-60 mt-1">
                  {new Date(room.last_message_time).toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' })}
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* چت */}
      <div 
        id="chat" 
        className={`${isMobile && !selectedRoom ? 'hidden' : 'flex'} flex-col flex-1 bg-gradient-to-br from-indigo-50 to-purple-50`}
      >
        {selectedRoom ? (
          <>
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4 flex items-center gap-3">
              {isMobile && (
                <button onClick={goBack} className="text-white">
                  <ArrowLeft className="w-6 h-6" />
                </button>
              )}
              <User className="w-8 h-8" />
              <div>
                <div className="font-bold">{selectedRoom.user_name}</div>
                <div className="text-xs opacity-80">{selectedRoom.user_mobile}</div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((msg) => (
                <div key={`${msg.id}-${msg.created_at}`} className={`flex ${msg.is_admin ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-xs px-4 py-3 rounded-2xl shadow-md break-words ${
                      msg.is_admin
                        ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white'
                        : 'bg-white border border-gray-200 text-gray-800'
                    }`}
                  >
                    {msg.message_text && <p className="text-sm">{msg.message_text}</p>}
                    {msg.file_url && (
                      <div className="mt-3">
                        {msg.file_type === 'image' ? (
                          <img src={msg.file_url} alt="" className="max-w-full rounded-xl cursor-pointer" onClick={() => window.open(msg.file_url, '_blank')} />
                        ) : (
                          <audio controls src={msg.file_url} className="w-full rounded-xl" />
                        )}
                      </div>
                    )}
                    <div className="text-xs mt-1 opacity-70 flex items-center gap-1 justify-end">
                      {new Date(msg.created_at).toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' })}
                      {msg.is_admin && (
                        msg.is_read ? <CheckCheck className="w-4 h-4" /> : <Check className="w-4 h-4" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-white px-4 py-3 rounded-2xl shadow-sm">
                    <div className="flex gap-1">
                      <motion.div animate={{ y: [0, -4, 0] }} transition={{ repeat: Infinity, duration: 0.6 }} className="w-2 h-2 bg-gray-400 rounded-full" />
                      <motion.div animate={{ y: [0, -4, 0] }} transition={{ repeat: Infinity, duration: 0.6, delay: 0.1 }} className="w-2 h-2 bg-gray-400 rounded-full" />
                      <motion.div animate={{ y: [0, -4, 0] }} transition={{ repeat: Infinity, duration: 0.6, delay: 0.2 }} className="w-2 h-2 bg-gray-400 rounded-full" />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="bg-white/90 backdrop-blur p-4 border-t">
              {file && (
                <div className="flex items-center gap-2 text-xs text-green-600 mb-2">
                  <Paperclip className="w-4 h-4" />
                  <span>{file.name}</span>
                  <button onClick={() => setFile(null)} className="text-red-500">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                  placeholder="پیام خود را بنویسید..."
                  className="flex-1 px-4 py-3 bg-gray-100 rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                />
                <input ref={fileInputRef} type="file" accept="image/*,audio/*" onChange={(e) => setFile(e.target.files[0])} className="hidden" />
                <button onClick={() => fileInputRef.current.click()} className="p-3 bg-gray-200 rounded-full hover:bg-gray-300">
                  <Paperclip className="w-5 h-5 text-gray-600" />
                </button>
                <button onClick={sendMessage} className="p-3 bg-indigo-600 text-white rounded-full hover:bg-indigo-700">
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <p className="text-lg">یک کاربر را انتخاب کنید</p>
          </div>
        )}
      </div>
    </div>
  );
}

